import {LAYER_TYPES, LayerOptions} from '../../../../../shared/image-layers/layer.types';
import {BaseLayer} from '../../../../../shared/image-layers/layers/BaseLayer';
import {action, observable} from 'mobx';
import {ICON_PATHS} from '../../icons';
import {
    MeshCapablePointOfInterest,
    MeshFrequencySource,
    filterCapturedPointsOfInterest,
    getMeshPointFrequency,
    getMeshPointLabel,
    isMeshPoint,
    truncateLabel
} from '../../../mesh-points.utils';

const BASE_FONT_SIZE = 12;
const BASE_ICON_SIZE = 11;
const BASE_ICON_TEXT_GAP = 8;
const BASE_ROW_GAP = 16;
const BASE_PADDING = 16;
const BASE_BORDER_RADIUS = 10;
const BASE_RSSI_BAR_HEIGHT = 36;
const BASE_RSSI_BAR_WIDTH = 400;
const BASE_RSSI_BOTTOM_PADDING = 10;
const BASE_GAP_ABOVE_RSSI = 10;

const BOX_BACKGROUND = 'rgba(31, 41, 55, 0.7)';
const BOX_BORDER_COLOR = 'rgba(144, 202, 249, 1)';
const BOX_BORDER_WIDTH = 1;
const TEXT_COLOR = '#FFFFFF';

let cachedMeshPath: Path2D | undefined;

export interface MarkerLegendItem {
    label: string;
    color: string;
    isMesh?: boolean;
}

export interface MarkersLegendLayerOptions extends LayerOptions {
    id?: string;
    bottomOffset?: number;
}

export class MarkersLegendLayer extends BaseLayer {
    @observable public items: MarkerLegendItem[] = [];

    private readonly bottomOffset: number;

    constructor(options?: MarkersLegendLayerOptions) {
        super(options?.id ?? 'markers-legend', LAYER_TYPES.MARKERS_LEGEND, {
            visible: options?.visible ?? true,
            opacity: options?.opacity ?? 1,
            zIndex: options?.zIndex ?? 2
        });

        this.bottomOffset = options?.bottomOffset ?? 56;
    }

    @action
    setItems(items: MarkerLegendItem[]): void {
        this.items = items;
        this.invalidate();
    }

    @action
    setItemsFromPointsOfInterest(
        pointsOfInterest: MeshCapablePointOfInterest[],
        imageUrls: MeshFrequencySource[] = []
    ): void {
        let meshIndex = 0;

        this.items = filterCapturedPointsOfInterest(pointsOfInterest)
            .filter((poi) => !!poi.type)
            .map((poi) => {
                if (isMeshPoint(poi)) {
                    meshIndex += 1;
                    const frequency = getMeshPointFrequency(poi, imageUrls);

                    return {
                        label: getMeshPointLabel(poi, meshIndex, frequency),
                        color: poi.color,
                        isMesh: true
                    };
                }

                return {
                    label: this.formatLabel(poi.type!),
                    color: poi.color
                };
            });

        this.invalidate();
    }

    private formatLabel(type: string): string {
        const formatted = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
        return truncateLabel(formatted);
    }

    render(
        ctx: CanvasRenderingContext2D,
        containerWidth: number,
        containerHeight: number,
        renderOptions?: Record<string, any>
    ): void {
        if (this.items.length === 0) return;

        const isMergeMode = renderOptions?.widthRatio !== undefined;
        const scaleFactor = isMergeMode
            ? (containerWidth * renderOptions!.widthRatio) / (renderOptions!.baseWidth || 350)
            : 1;

        const fontSize = Math.round(BASE_FONT_SIZE * scaleFactor);
        const iconSize = Math.round(BASE_ICON_SIZE * scaleFactor);
        const iconTextGap = Math.round(BASE_ICON_TEXT_GAP * scaleFactor);
        const rowGap = Math.round(BASE_ROW_GAP * scaleFactor);
        const padding = Math.round(BASE_PADDING * scaleFactor);
        const borderRadius = Math.round(BASE_BORDER_RADIUS * scaleFactor);

        ctx.font = `500 ${fontSize}px "Open Sans", sans-serif`;

        const textMetrics = this.items.map((item) => ctx.measureText(item.label));
        const maxTextWidth = Math.max(...textMetrics.map((m) => m.width));
        const boxWidth = padding * 2 + iconSize + iconTextGap + maxTextWidth;
        const boxHeight = padding * 2 + this.items.length * fontSize + (this.items.length - 1) * rowGap;

        let rssiBarX: number;
        let rssiBarY: number;
        const gap = Math.round(BASE_GAP_ABOVE_RSSI * scaleFactor);

        if (isMergeMode) {
            const rssiBarWidth = Math.round(containerWidth * renderOptions!.widthRatio);
            const rssiBarHeight = Math.round(BASE_RSSI_BAR_HEIGHT * scaleFactor);
            const mergeBottomPadding = renderOptions!.bottomPadding ?? 20;
            const centerOffset = renderOptions!.centerOffset ?? 0;
            rssiBarX = Math.round((containerWidth - rssiBarWidth) / 2) + centerOffset;
            rssiBarY = containerHeight - rssiBarHeight - mergeBottomPadding;
        } else {
            const rssiBarWidth = BASE_RSSI_BAR_WIDTH;
            const rssiBarHeight = BASE_RSSI_BAR_HEIGHT;
            rssiBarX = Math.round((containerWidth - rssiBarWidth) / 2);
            rssiBarY = containerHeight - rssiBarHeight - BASE_RSSI_BOTTOM_PADDING;
        }

        const x = rssiBarX;
        const y = rssiBarY - gap - boxHeight;

        this.renderBox(ctx, x, y, boxWidth, boxHeight, borderRadius);
        this.renderItems(ctx, x + padding, y + padding, fontSize, iconSize, iconTextGap, rowGap);
    }

    private renderBox(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        width: number,
        height: number,
        borderRadius: number
    ): void {
        ctx.beginPath();
        ctx.roundRect(x, y, width, height, borderRadius);
        ctx.fillStyle = BOX_BACKGROUND;
        ctx.fill();
        ctx.strokeStyle = BOX_BORDER_COLOR;
        ctx.lineWidth = BOX_BORDER_WIDTH;
        ctx.stroke();
    }

    private renderItems(
        ctx: CanvasRenderingContext2D,
        x: number,
        y: number,
        fontSize: number,
        iconSize: number,
        iconTextGap: number,
        rowGap: number
    ): void {
        this.items.forEach((item, index) => {
            const rowY = y + index * (fontSize + rowGap);
            const circleRadius = iconSize / 2;
            const circleCenterX = x + circleRadius;
            const circleCenterY = rowY + fontSize / 2;

            if (item.isMesh) {
                this.drawMeshIcon(ctx, x, circleCenterY, iconSize, item.color);
            } else {
                ctx.beginPath();
                ctx.arc(circleCenterX, circleCenterY, circleRadius, 0, Math.PI * 2);
                ctx.fillStyle = item.color;
                ctx.fill();
            }

            ctx.fillStyle = TEXT_COLOR;
            ctx.font = `500 ${fontSize}px "Open Sans", sans-serif`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText(item.label, x + iconSize + iconTextGap, circleCenterY);
        });
    }

    private drawMeshIcon(ctx: CanvasRenderingContext2D, x: number, centerY: number, size: number, color: string): void {
        const scale = size / ICON_PATHS.mesh.viewBox;

        ctx.save();
        ctx.translate(x, centerY - size / 2);
        ctx.scale(scale, scale);
        ctx.fillStyle = color;
        if (typeof Path2D !== 'undefined') {
            if (!cachedMeshPath) {
                cachedMeshPath = new Path2D(ICON_PATHS.mesh.path);
            }
            ctx.fill(cachedMeshPath);
        }
        ctx.restore();
    }
}
